/**
 * SourceOwnerSelect — Phase 2 PR 20.
 *
 * The Source-owner assignment surface. Two visual modes:
 *
 *   - `variant="full"` (default) — renders an inline button group with
 *     a search-filterable dropdown menu of org members + an "Unassign"
 *     row. Mounted on the Source detail Overview tab.
 *
 *   - `variant="compact"` — renders just the avatar + name (or
 *     "Unassigned" placeholder) as a button that opens the same menu.
 *     Used on /sources list rows where horizontal real estate is tight.
 *
 * The candidate list is fetched lazily on first menu open and cached on
 * the component instance. Refresh happens when the menu re-opens after
 * a successful assignment, so a new invite acceptance shows up without
 * a page reload.
 *
 * Optimistic update model: on PATCH success, fires `onChange` with the
 * new owner triple (or null) so the parent can swap state immediately
 * without re-fetching the source row. On failure, surfaces a toast and
 * leaves the parent state untouched.
 */

import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Loader2, UserX } from "lucide-react";
import {
  api,
  type SpineOwnerCandidate,
  type SpineSourceOwner,
} from "@/lib/api";
import { toast } from "@/lib/toast";

interface SourceOwnerSelectProps {
  sourceId: string;
  /** Currently-assigned owner, or null if unassigned. */
  owner: SpineSourceOwner | null;
  /** Display variant. `full` shows an inline pill+menu; `compact` is avatar-only. */
  variant?: "full" | "compact";
  /**
   * Called after a successful PATCH so the parent can update its own
   * state without re-fetching the whole source row.
   */
  onChange?: (next: SpineSourceOwner | null) => void;
  /** Optional disable hook for readonly viewers; the menu still opens but every option is disabled. */
  disabled?: boolean;
}

/**
 * Display helper — prefer `name`, fall back to the local-part of `email`,
 * then the full email. Mirrors the convention the classify control uses
 * for "who decided" attribution on the audit log (PR 21).
 */
function ownerDisplayName(o: { name: string | null; email: string }): string {
  if (o.name && o.name.trim().length > 0) return o.name;
  const local = o.email.split("@")[0];
  return local || o.email;
}

/**
 * Single-letter avatar derived from the display name. Color is content-
 * addressed (hash → palette index) so the same user always gets the
 * same swatch across the app — recognisable without rendering an image.
 */
const AVATAR_PALETTE = [
  "bg-amber-100 text-amber-700",
  "bg-emerald-100 text-emerald-700",
  "bg-sky-100 text-sky-700",
  "bg-rose-100 text-rose-700",
  "bg-violet-100 text-violet-700",
  "bg-teal-100 text-teal-700",
] as const;

function ownerSwatch(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length]!;
}

function OwnerAvatar({
  owner,
  size = "md",
}: {
  owner: SpineSourceOwner | null;
  size?: "sm" | "md";
}) {
  const dim = size === "sm" ? "w-5 h-5 text-[10px]" : "w-6 h-6 text-xs";
  if (!owner) {
    return (
      <span
        className={`inline-flex items-center justify-center rounded-full bg-slate-100 text-slate-400 ${dim}`}
        aria-hidden="true"
      >
        <UserX className={size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5"} />
      </span>
    );
  }
  const letter = ownerDisplayName(owner).charAt(0).toUpperCase();
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full font-semibold ${ownerSwatch(
        owner.id
      )} ${dim}`}
      aria-hidden="true"
    >
      {letter}
    </span>
  );
}

export function SourceOwnerSelect({
  sourceId,
  owner,
  variant = "full",
  onChange,
  disabled = false,
}: SourceOwnerSelectProps) {
  const [open, setOpen] = useState(false);
  const [candidates, setCandidates] = useState<SpineOwnerCandidate[] | null>(null);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [search, setSearch] = useState("");
  const [pending, setPending] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Lazy-load org members the first time the menu opens. Re-fetch on
  // every subsequent open so freshly invited users appear without a
  // page reload.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingCandidates(true);
    api
      .getSourceOwners()
      .then((resp) => {
        if (!cancelled) setCandidates(resp.users);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        toast.error(
          err instanceof Error ? err.message : "Failed to load team members"
        );
      })
      .finally(() => {
        if (!cancelled) setLoadingCandidates(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Close on outside-click + Escape so the menu doesn't strand itself.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  async function assign(target: SpineOwnerCandidate | null) {
    if (disabled || pending) return;
    setPending(true);
    try {
      await api.setSourceOwner(sourceId, target?.id ?? null);
      const next: SpineSourceOwner | null = target
        ? { id: target.id, name: target.name, email: target.email }
        : null;
      onChange?.(next);
      toast.success(target ? `Assigned to ${ownerDisplayName(target)}` : "Unassigned");
      setOpen(false);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to update owner");
    } finally {
      setPending(false);
    }
  }

  const filtered = (candidates ?? []).filter((c) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (c.name?.toLowerCase().includes(q) ?? false) ||
      c.email.toLowerCase().includes(q)
    );
  });

  const trigger =
    variant === "compact" ? (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-1.5 py-1 rounded-md hover:bg-slate-100 disabled:opacity-50"
        aria-label={
          owner ? `Owner: ${ownerDisplayName(owner)}. Click to reassign.` : "Unassigned. Click to assign."
        }
      >
        <OwnerAvatar owner={owner} size="sm" />
        {pending ? <Loader2 className="w-3 h-3 animate-spin text-slate-400" /> : null}
      </button>
    ) : (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 text-sm text-slate-700 disabled:opacity-50"
      >
        <OwnerAvatar owner={owner} />
        <span className="font-medium truncate max-w-[12rem]">
          {owner ? ownerDisplayName(owner) : "Unassigned"}
        </span>
        {pending ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
        )}
      </button>
    );

  return (
    <div ref={menuRef} className="relative inline-block">
      {trigger}
      {open ? (
        <div
          className="absolute z-20 mt-1 right-0 w-64 rounded-md border border-slate-200 bg-white shadow-lg"
          role="menu"
        >
          <div className="p-2 border-b border-slate-100">
            <input
              type="text"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search team members..."
              className="w-full px-2 py-1 text-sm border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-amber-400"
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {loadingCandidates && candidates === null ? (
              <div className="px-3 py-2 text-xs text-slate-500 flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin" />
                Loading team...
              </div>
            ) : null}

            {/* Unassign row pinned at the top so it's reachable with one
              * key/touch when the user wants to clear ownership. */}
            <button
              type="button"
              disabled={disabled || pending}
              onClick={() => assign(null)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              role="menuitem"
            >
              <OwnerAvatar owner={null} size="sm" />
              <span className="flex-1 text-left">Unassigned</span>
              {owner === null ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : null}
            </button>

            {filtered.length === 0 && !loadingCandidates && search.length > 0 ? (
              <div className="px-3 py-2 text-xs text-slate-400">
                No matches for "{search}"
              </div>
            ) : null}

            {filtered.map((cand) => {
              const isCurrent = owner?.id === cand.id;
              return (
                <button
                  key={cand.id}
                  type="button"
                  disabled={disabled || pending || isCurrent}
                  onClick={() => assign(cand)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  role="menuitem"
                >
                  <OwnerAvatar
                    owner={{ id: cand.id, name: cand.name, email: cand.email }}
                    size="sm"
                  />
                  <span className="flex-1 text-left truncate">
                    <span className="font-medium">{ownerDisplayName(cand)}</span>
                    {cand.role === "readonly" ? (
                      <span className="ml-1.5 text-[10px] uppercase tracking-wide text-slate-400">
                        viewer
                      </span>
                    ) : null}
                  </span>
                  {isCurrent ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Read-only display variant. Used in evidence rows / activity log where
 * we want to show "who owns this" without offering a click-to-reassign.
 */
export function SourceOwnerBadge({
  owner,
  size = "sm",
}: {
  owner: SpineSourceOwner | null;
  size?: "sm" | "md";
}) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-slate-600">
      <OwnerAvatar owner={owner} size={size} />
      <span className="truncate">
        {owner ? ownerDisplayName(owner) : "Unassigned"}
      </span>
    </span>
  );
}
